// components/admin/AdminConversations.js
import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, User, Clock, Search } from 'react-feather';
import AuthService from '../../services/authService';

const AdminConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  
  const messagesEndRef = useRef(null);
  const API_URL = 'https://back-end-x7tx.onrender.com/api';

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    filterConversations();
  }, [userSearchTerm, conversations]);

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        // Filter to show ALL admin conversations (any admin as helper)
        // This includes conversations where ANY admin is helping users
        const adminConversations = data.data.filter(conv => 
          conv.helperType === 'admin' || 
          (!conv.helper && !conv.helperType) // Include unassigned conversations
        );
        
        console.log('Total conversations:', data.data.length);
        console.log('Admin conversations found:', adminConversations.length);
        setConversations(adminConversations);
        setFilteredConversations(adminConversations);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Error loading conversations');
    } finally {
      setIsLoading(false);
    }
  };

  const filterConversations = () => {
    let filtered = [...conversations];

    // Filter by user search
    if (userSearchTerm) {
      const search = userSearchTerm.toLowerCase();
      filtered = filtered.filter(conv => 
        conv.user && (
          conv.user.name.toLowerCase().includes(search) ||
          conv.user.email.toLowerCase().includes(search)
        )
      );
    }

    setFilteredConversations(filtered);
  };

  const loadConversation = async (conversationId) => {
    setIsLoadingMessages(true);
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSelectedConversation(data.data.conversation);
        setMessages(data.data.messages || []);
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Failed to load messages');
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;

    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${selectedConversation._id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          content: messageText.trim()
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessages([...messages, data.data]);
        setMessageText('');
        setTimeout(scrollToBottom, 100);
        // Refresh conversations to update last message
        fetchConversations();
      } else {
        setError(data.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Error sending message');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return `${Math.floor(diffInHours * 60)}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return messageDate.toLocaleDateString();
    }
  };

  const styles = {
    container: {
      display: 'flex',
      height: 'calc(100vh - 80px)',
      backgroundColor: '#0f1419',
      borderRadius: '12px',
      overflow: 'hidden',
      border: '1px solid rgba(99, 102, 241, 0.2)'
    },
    sidebar: {
      width: '380px',
      borderRight: '1px solid rgba(99, 102, 241, 0.2)',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'rgba(15, 20, 25, 0.8)'
    },
    header: {
      padding: '20px',
      borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
      backgroundColor: 'rgba(15, 20, 25, 0.8)'
    },
    title: {
      margin: '0 0 16px 0',
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    searchSection: {
      padding: '16px',
      borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
      backgroundColor: 'rgba(15, 20, 25, 0.8)'
    },
    searchInput: {
      width: '100%',
      padding: '10px 12px 10px 40px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '0.9rem',
      outline: 'none'
    },
    conversationsList: {
      flex: 1,
      overflowY: 'auto',
      padding: '8px 0'
    },
    conversationItem: (isSelected) => ({
      padding: '16px 20px',
      borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
      cursor: 'pointer',
      backgroundColor: isSelected ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
      transition: 'background-color 0.2s'
    }),
    conversationInfo: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px'
    },
    avatar: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      backgroundColor: 'rgba(99, 102, 241, 0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      border: '2px solid rgba(99, 102, 241, 0.3)',
      color: '#a5b4fc',
      fontSize: '1.2rem',
      fontWeight: '600'
    },
    conversationDetails: {
      flex: 1,
      minWidth: 0
    },
    userName: (hasUnread) => ({
      fontWeight: hasUnread ? '700' : '600',
      color: '#ffffff',
      fontSize: '0.95rem',
      marginBottom: '4px'
    }),
    lastMessage: {
      fontSize: '0.85rem',
      color: 'rgba(255, 255, 255, 0.6)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      marginBottom: '6px'
    },
    conversationMeta: {
      display: 'flex',
      gap: '12px',
      fontSize: '0.75rem',
      color: 'rgba(255, 255, 255, 0.5)'
    },
    badge: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '0.7rem',
      fontWeight: '600',
      textTransform: 'capitalize'
    },
    messagePanel: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#0f1419'
    },
    conversationHeader: {
      padding: '20px',
      borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
      backgroundColor: 'rgba(15, 20, 25, 0.8)'
    },
    conversationTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: '#ffffff',
      marginBottom: '4px'
    },
    conversationSubtitle: {
      fontSize: '0.9rem',
      color: 'rgba(255, 255, 255, 0.6)'
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      backgroundColor: '#0f1419'
    },
    message: (isAdmin) => ({
      display: 'flex',
      justifyContent: isAdmin ? 'flex-end' : 'flex-start',
      marginBottom: '16px'
    }),
    messageBubble: (isAdmin) => ({
      maxWidth: '70%',
      padding: '12px 16px',
      borderRadius: '12px',
      backgroundColor: isAdmin ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.05)',
      border: isAdmin ? '1px solid rgba(99, 102, 241, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
      color: '#ffffff'
    }),
    messageHeader: {
      fontSize: '0.75rem',
      color: 'rgba(255, 255, 255, 0.6)',
      marginBottom: '4px',
      fontWeight: '600',
      textAlign: 'left'
    },
    messageContent: {
      fontSize: '0.95rem',
      lineHeight: '1.5',
      wordWrap: 'break-word',
      textAlign: 'left'
    },
    messageTime: {
      fontSize: '0.7rem',
      color: 'rgba(255, 255, 255, 0.5)',
      marginTop: '6px',
      textAlign: 'right'
    },
    messageInputContainer: {
      padding: '20px',
      borderTop: '1px solid rgba(99, 102, 241, 0.2)',
      backgroundColor: 'rgba(15, 20, 25, 0.8)'
    },
    messageInputWrapper: {
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-end'
    },
    messageInput: {
      flex: 1,
      padding: '12px 16px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '0.95rem',
      resize: 'none',
      outline: 'none',
      minHeight: '48px',
      maxHeight: '120px',
      fontFamily: 'inherit'
    },
    sendButton: {
      padding: '12px 24px',
      backgroundColor: '#6366f1',
      border: 'none',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s',
      height: '48px'
    },
    emptyState: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'rgba(255, 255, 255, 0.5)',
      padding: '40px'
    },
    emptyStateIcon: {
      width: '80px',
      height: '80px',
      marginBottom: '20px',
      opacity: 0.3
    },
    emptyStateTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: 'rgba(255, 255, 255, 0.7)',
      marginBottom: '8px'
    },
    emptyStateText: {
      fontSize: '0.9rem',
      color: 'rgba(255, 255, 255, 0.5)',
      textAlign: 'center'
    }
  };

  return (
    <div style={styles.container}>
      {/* Left Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            <MessageCircle size={24} />
            Admin Conversations
          </h2>
        </div>

        {/* Search */}
        <div style={styles.searchSection}>
          <div style={{ position: 'relative' }}>
            <Search 
              size={18} 
              style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: 'rgba(255, 255, 255, 0.4)'
              }} 
            />
            <input
              type="text"
              placeholder="Search users..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>
        </div>

        {/* Conversations List */}
        <div style={styles.conversationsList}>
          {isLoading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>
              Loading conversations...
            </div>
          ) : filteredConversations.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>
              {userSearchTerm ? 'No matching conversations' : 'No conversations yet'}
            </div>
          ) : (
            filteredConversations
              .filter(conv => conv.user) // Skip conversations with null user
              .map(conv => (
                <div
                  key={conv._id}
                  onClick={() => loadConversation(conv._id)}
                  style={styles.conversationItem(selectedConversation?._id === conv._id)}
                  onMouseEnter={(e) => {
                    if (selectedConversation?._id !== conv._id) {
                      e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedConversation?._id !== conv._id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div style={styles.conversationInfo}>
                    <div style={styles.avatar}>
                      {conv.user.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={styles.conversationDetails}>
                      {conv.helper && (
                        <div style={{
                          fontSize: '0.75rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                          marginBottom: '4px'
                        }}>
                          Admin: {conv.helper.name}
                        </div>
                      )}
                      <div style={styles.userName(conv.unreadCount > 0)}>
                        {conv.user.name}
                        {conv.user.leadTier && (
                          <span style={{
                            ...styles.badge,
                            marginLeft: '8px',
                            backgroundColor: {
                              bronze: 'rgba(205, 127, 50, 0.2)',
                              silver: 'rgba(192, 192, 192, 0.2)',
                              gold: 'rgba(255, 215, 0, 0.2)',
                              platinum: 'rgba(229, 228, 226, 0.2)'
                            }[conv.user.leadTier],
                            color: {
                              bronze: '#cd7f32',
                              silver: '#c0c0c0',
                              gold: '#ffd700',
                              platinum: '#e5e4e2'
                            }[conv.user.leadTier]
                          }}>
                            {conv.user.leadTier}
                          </span>
                        )}
                        {conv.unreadCount > 0 && (
                          <span style={{
                            marginLeft: '8px',
                            backgroundColor: '#ef4444',
                            color: '#ffffff',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.7rem',
                            fontWeight: '700'
                          }}>
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                      {conv.lastMessage && (
                        <div style={styles.lastMessage}>
                          {conv.lastMessage.content}
                        </div>
                      )}
                      <div style={styles.conversationMeta}>
                        <span>
                          <Clock size={12} style={{ marginRight: '4px' }} />
                          {formatTime(conv.updatedAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {/* Right Panel - Messages */}
      <div style={styles.messagePanel}>
        {!selectedConversation ? (
          <div style={styles.emptyState}>
            <MessageCircle size={80} style={styles.emptyStateIcon} />
            <div style={styles.emptyStateTitle}>Select a Conversation</div>
            <div style={styles.emptyStateText}>
              Choose a user from the list to start messaging
            </div>
          </div>
        ) : (
          <>
            <div style={styles.conversationHeader}>
              <div style={styles.conversationTitle}>
                {selectedConversation.user?.name || 'Unknown User'}
              </div>
              <div style={styles.conversationSubtitle}>
                {selectedConversation.user?.email || ''}
                {selectedConversation.user?.leadTier && (
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'capitalize',
                    backgroundColor: {
                      bronze: 'rgba(205, 127, 50, 0.2)',
                      silver: 'rgba(192, 192, 192, 0.2)',
                      gold: 'rgba(255, 215, 0, 0.2)',
                      platinum: 'rgba(229, 228, 226, 0.2)'
                    }[selectedConversation.user.leadTier],
                    color: {
                      bronze: '#cd7f32',
                      silver: '#c0c0c0',
                      gold: '#ffd700',
                      platinum: '#e5e4e2'
                    }[selectedConversation.user.leadTier]
                  }}>
                    {selectedConversation.user.leadTier}
                  </span>
                )}
              </div>
            </div>

            <div style={styles.messagesContainer}>
              {isLoadingMessages ? (
                <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', padding: '40px' }}>
                  Loading messages...
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', padding: '40px' }}>
                  No messages yet. Start the conversation!
                </div>
              ) : (
                messages.map((msg) => {
                  const isAdmin = msg.senderRole === 'admin';
                  const isCurrentUser = msg.sender._id === AuthService.getCurrentUser().id;
                  return (
                    <div key={msg._id} style={styles.message(isAdmin)}>
                      <div style={styles.messageBubble(isAdmin)}>
                        <div style={styles.messageHeader}>
                          {isAdmin 
                            ? (isCurrentUser ? 'You (Admin)' : `${msg.sender.name} (Admin)`)
                            : msg.sender.name
                          }
                        </div>
                        <div style={styles.messageContent}>
                          {msg.content}
                        </div>
                        <div style={styles.messageTime}>
                          {new Date(msg.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div style={styles.messageInputContainer}>
              <div style={styles.messageInputWrapper}>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  style={styles.messageInput}
                  disabled={isSending}
                />
                <button
                  onClick={sendMessage}
                  disabled={!messageText.trim() || isSending}
                  style={{
                    ...styles.sendButton,
                    opacity: !messageText.trim() || isSending ? 0.5 : 1,
                    cursor: !messageText.trim() || isSending ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (messageText.trim() && !isSending) {
                      e.currentTarget.style.backgroundColor = '#4f46e5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#6366f1';
                  }}
                >
                  <Send size={18} />
                  {isSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminConversations;